/**
 * Shared style tokens for the ordering area.
 * Import from this module so all ordering surfaces share the same colors,
 * button shapes, and semantic intent — no more copy-pasted STATUS_STYLES.
 */

// ── Status color palette ─────────────────────────────────────────────────────
// Canonical set: green = sent/received, blue = engaged, amber = draft/pending,
// red = error/bounced/canceled, purple = superseded, grey = voided.
export const ORDER_STATUS_STYLES = {
  sent:             { bg: '#E8F5E9', fg: '#2E7D32', label: 'Sent' },
  delivered:        { bg: '#E8F5E9', fg: '#2E7D32', label: 'Delivered' },
  confirmed:        { bg: '#E8F5E9', fg: '#2E7D32', label: 'Confirmed' },
  approved:         { bg: '#E8F5E9', fg: '#2E7D32', label: 'Approved' },
  received:         { bg: '#E8F5E9', fg: '#1b5e20', label: 'Received' },
  closed:           { bg: '#C8E6C9', fg: '#1b5e20', label: 'Closed' },
  opened:           { bg: '#E3F2FD', fg: '#0d47a1', label: 'Opened' },
  clicked:          { bg: '#E3F2FD', fg: '#0d47a1', label: 'Clicked' },
  sending:          { bg: '#FFF3E0', fg: '#E65100', label: 'Sending…' },
  pending_approval: { bg: '#FFF3E0', fg: '#E65100', label: 'Pending Approval' },
  blocked_no_email: { bg: '#FFF3E0', fg: '#E65100', label: 'No Email' },
  draft:            { bg: '#FFF4E0', fg: '#8A5A00', label: 'Draft' },
  blocked:          { bg: '#FDECEA', fg: '#b71c1c', label: 'Blocked' },
  bounced:          { bg: '#FDECEA', fg: '#b71c1c', label: 'Bounced' },
  failed:           { bg: '#FDECEA', fg: '#b71c1c', label: 'Failed' },
  complained:       { bg: '#FDECEA', fg: '#b71c1c', label: 'Complained' },
  delivery_delayed: { bg: '#FFF3CD', fg: '#856404', label: 'Delayed' },
  error:            { bg: '#FDECEA', fg: '#b71c1c', label: 'Error' },
  canceled:         { bg: '#FDECEA', fg: '#b71c1c', label: 'Canceled' },
  revised:          { bg: '#EDE7F6', fg: '#4527A0', label: 'Superseded' },
  replaced:         { bg: '#EDE7F6', fg: '#4527A0', label: 'Replaced' },
  voided:           { bg: '#F5F5F5', fg: '#757575', label: 'Voided' },
};

// ── Draft card theme ──────────────────────────────────────────────────────────
// Single source of truth for the amber draft-card palette.
export const DRAFT_CARD_THEME = {
  bg:     '#FFF4E0',
  border: '#E6B866',
  fg:     '#8A5A00',
};

// CSS keyframes for the slow "Review to send" pulse.
// Consume inside a component <style> block.
// Animation is gated on prefers-reduced-motion via a media query so users
// who opt out of motion see a static button instead.
export const DRAFT_PULSE_CSS = `
@keyframes po-draft-pulse {
  0%   { opacity: 1;   box-shadow: 0 0 0 0   rgba(230, 184, 102, 0.45); }
  50%  { opacity: 0.82; box-shadow: 0 0 0 5px rgba(230, 184, 102, 0);   }
  100% { opacity: 1;   box-shadow: 0 0 0 0   rgba(230, 184, 102, 0);   }
}
@media (prefers-reduced-motion: no-preference) {
  .po-review-to-send-btn {
    animation: po-draft-pulse 2s ease-in-out infinite;
  }
}
`;

// ── Button tokens ─────────────────────────────────────────────────────────────
// All buttons share the same shape; only color/weight differs by role.
const BTN_BASE = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 38,
  padding: '9px 18px',
  borderRadius: 10,
  fontWeight: 700,
  fontSize: 13,
  lineHeight: 1,
  cursor: 'pointer',
  fontFamily: 'inherit',
  whiteSpace: 'nowrap',
  textDecoration: 'none',
};

/** Dominant action — e.g. Send to Vendor, Save, Mark Received */
export const BTN_PRIMARY = {
  ...BTN_BASE,
  background: '#5A4A42',
  color: '#fff',
  border: 'none',
};

/** Send / confirm action colored green (used when a PO is sendable) */
export const BTN_PRIMARY_GREEN = {
  ...BTN_BASE,
  background: '#2E7D32',
  color: '#fff',
  border: 'none',
};

/** Supporting action — e.g. Save Draft, Refresh Vendor, Print, Duplicate */
export const BTN_SECONDARY = {
  ...BTN_BASE,
  background: '#fff',
  color: '#5A4A42',
  border: '1.5px solid #d5c9c0',
};

/** Destructive action outlined — e.g. Delete Draft, Cancel PO (not yet committed) */
export const BTN_DANGER = {
  ...BTN_BASE,
  background: '#fff',
  color: '#b71c1c',
  border: '1.5px solid #ef9a9a',
};

/** Destructive action filled — e.g. confirm delete inside a modal */
export const BTN_DANGER_FILLED = {
  ...BTN_BASE,
  background: '#b71c1c',
  color: '#fff',
  border: 'none',
};

/** Ghost/tertiary — low-emphasis text actions */
export const BTN_TERTIARY = {
  ...BTN_BASE,
  background: 'none',
  color: '#7b6b61',
  border: 'none',
  padding: '9px 12px',
  fontWeight: 600,
};

/** Disabled — not interactive */
export const BTN_DISABLED = {
  ...BTN_BASE,
  background: '#f7f1ea',
  color: '#b0a098',
  border: '1.5px solid #e8e0d5',
  cursor: 'not-allowed',
};

// ── Status pill style factory ─────────────────────────────────────────────────
export function statusPillStyle(statusKey) {
  const s = ORDER_STATUS_STYLES[statusKey] || { bg: '#F5F5F5', fg: '#5A4A42' };
  return {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: 999,
    background: s.bg,
    color: s.fg,
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
  };
}

// ── Alert / feedback styles ───────────────────────────────────────────────────
export const ALERT_BASE = { borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13 };

export const ALERT_ERROR = {
  ...ALERT_BASE,
  background: '#fde8e8',
  border: '1.5px solid #ef9a9a',
  color: '#8b0000',
};

export const ALERT_SUCCESS = {
  ...ALERT_BASE,
  background: '#e8f5e9',
  border: '1.5px solid #66bb6a',
  color: '#1b5e20',
};

export const ALERT_WARNING = {
  ...ALERT_BASE,
  background: '#fff8e1',
  border: '1.5px solid #ffc107',
  color: '#7b4f00',
};

// ── Panel states ──────────────────────────────────────────────────────────────
export const PANEL_EMPTY = {
  padding: '40px 20px',
  textAlign: 'center',
  color: '#8B7B73',
  background: '#fff',
  border: '1.5px dashed #E9DFD6',
  borderRadius: 12,
  fontSize: 14,
};

export const PANEL_LOADING = {
  padding: '24px 0',
  textAlign: 'center',
  color: '#8B7B73',
  fontSize: 14,
};

export const PANEL_ERROR = {
  padding: '12px 16px',
  color: '#b71c1c',
  background: '#FDECEA',
  borderRadius: 8,
  border: '1px solid #f5c2bd',
  fontSize: 13,
};

// ── Focus ring CSS ────────────────────────────────────────────────────────────
// Inject this inside a component's <style> block. Targets interactive elements
// directly so no wrapper class is needed — works as-is for button/link/input.
export const FOCUS_RING_CSS = `
  button:focus-visible,
  a:focus-visible,
  input:focus-visible,
  select:focus-visible,
  textarea:focus-visible,
  [tabindex]:focus-visible {
    outline: 2.5px solid #5A4A42 !important;
    outline-offset: 2px !important;
  }
`;
